
import {
  Vue, Component, Prop, PropSync, Ref, Watch,
} from 'vue-property-decorator';
import { InputValidationRules } from 'vuetify';
import { debounce } from 'ts-debounce';
import { ShipTo, PredictionAddressDto } from '@/features/shipment/types';
import { CountryInfo, VTextField } from '@/features/common/types';
import {
  getPostalCodeRules,
  isProvinceRequired,
  requiredRules,
  stripUnoCharFromPostalCode,
} from '@/features/common/helpers/validationRules';
import { getCarriersClarificationAddresses, getClarificationDetailFromMapAddress } from '../../services/shipment.services';
import { trackSelectAddressSuggestion, trackManualEntryAddress } from '../../helpers/tracked-events/google-address-integration';

@Component
export default class AddressInputsCollapse extends Vue {
  @Prop({ required: true }) location!: string;

  @Prop({ required: false, default: true }) autoCollapseEnabled!: boolean;

  @PropSync('data', { required: true }) syncedData!: ShipTo;

  @PropSync('forceShowInputs') syncedForceShowInputs!: boolean;

  @Ref('postalCodeInput') postalCodeInput!: HTMLFormElement;

  @Ref('addressInput') addressInput!: HTMLFormElement;

  private countryInputFocused = false;

  private mouseDownOnInputs = false;

  private fieldsFocused = false;

  private requiredRules = requiredRules;

  private showForm = false;

  private addressAutocomplete: {
    items: PredictionAddressDto[],
    isLoading: boolean,
    search: string,
  } = {
    items: [],
    isLoading: false,
    search: '',
  };

  get addressTo(): string {
    return this.autoCollapseEnabled && !this.fieldsFocused && !this.showForm
      ? this.getCombinedAddress()
      : this.syncedData.addr1;
  }

  set addressTo(val: string) {
    this.syncedData.addr1 = val;
  }

  get currentCountry(): CountryInfo | undefined {
    return this.$store.getters.getCountryFromCode(this.syncedData.countryCode);
  }

  get inputClass(): string {
    return `add-fields-${this.location.toLowerCase()}`;
  }

  // eslint-disable-next-line class-methods-use-this
  get inputs(): Array<HTMLInputElement> {
    return Array.from(document.querySelectorAll('.v-input'));
  }

  get provinceDetails(): {
    rules: InputValidationRules | null,
    suffix: string,
    label: string,
    } {
    const required = isProvinceRequired(this.syncedData.countryCode);
    const noSuffix = required || this.syncedData.province;
    const label = this.syncedData.countryCode === 'US'
      ? this.$t('labels.state') as string
      : this.$t('labels.province') as string;

    return {
      rules: required ? this.requiredRules : [],
      suffix: '',
      label: required ? `${label} *` : label,
    };
  }

  get postalCodeRules(): InputValidationRules {
    if (!this.currentCountry) return this.requiredRules;
    return getPostalCodeRules(this.currentCountry);
  }

  @Watch('addressAutocomplete.search', { immediate: true })
  private async searchAddresses(search: string) {
    if (!search) return;

    this.debounceSearchAddresses(search);
  }

  private debounceSearchAddresses = debounce(
    this.debounceSearchAddressesHandler, 500,
  );

  private async debounceSearchAddressesHandler(value: string) {
    this.addressAutocomplete.isLoading = true;
    const items = await getCarriersClarificationAddresses(value);
    this.addressAutocomplete.isLoading = false;
    if (!items) return;

    this.addressAutocomplete.items = [
      ...items,
    ];
  }

  private onAddAddressManually() {
    this.showForm = true;
    trackManualEntryAddress(this.$gtag);
  }

  private async onSelectAddress(address: PredictionAddressDto) {
    const { placeId } = address;

    if (!placeId) return;

    const addressDetail = await getClarificationDetailFromMapAddress(placeId);

    if (!addressDetail) return;

    const finalAddress = addressDetail
      .find((ad) => !!ad.postalCode && !!ad.countryCode) || addressDetail[0];
    if (!finalAddress) return;

    this.showForm = true;

    const {
      countryCode, postalCode, city, addr1, province,
    } = finalAddress;

    this.syncedData.countryCode = countryCode || '';
    this.syncedData.postalCode = postalCode || '';
    this.syncedData.city = city || '';
    this.syncedData.addr1 = addr1 || '';
    this.syncedData.province = province || '';
    trackSelectAddressSuggestion(this.$gtag);
  }

  @Watch('currentCountry')
  private validatePostalCodeInput() {
    this.$nextTick(() => {
      (this.$refs.postalCodeInput as VTextField).validate();
    });
  }

  @Watch('syncedForceShowInputs')
  private openInputs(val) {
    if (val) {
      this.fieldsFocused = true;

      setTimeout(() => {
        this.syncedForceShowInputs = false;
      }, 200);
    }
  }

  mounted(): void {
    document.addEventListener('mousedown', this.handleContainerMouseDown);
    document.addEventListener('mouseup', this.handleContainerMouseUp);

    // vuetify's inputs prevent propagation by default, so need to add listener manually
    this.inputs.forEach((el) => {
      el.addEventListener('mousedown', this.handleContainerMouseDown, true);
    });
  }

  destroyed(): void {
    document.removeEventListener('mousedown', this.handleContainerMouseDown);
    document.removeEventListener('mouseup', this.handleContainerMouseUp);

    this.inputs.forEach((el) => {
      el.removeEventListener('mousedown', this.handleContainerMouseDown);
    });
  }

  private isMouseEventInZone(event) {
    const clickedOnInput = this.$el.contains(event.target as Node);
    const clickedOnMenu = (event.target as HTMLElement).className.includes('v-list') && this.countryInputFocused;

    return clickedOnInput || clickedOnMenu;
  }

  private handleContainerMouseDown(event) {
    this.mouseDownOnInputs = this.isMouseEventInZone(event);
  }

  private handleContainerMouseUp(event) {
    if (!this.isMouseEventInZone(event) && !this.mouseDownOnInputs) {
      if (this.postalCodeInput.valid && this.addressInput.valid) {
        this.debouncedCollapseInputs();
      }
    }
  }

  private focusOutHandler() {
    this.debounceHandler();
  }

  private debounceHandler = debounce(this.disableFieldsFocus, 100);

  // eslint-disable-next-line class-methods-use-this
  private disableFieldsFocus() {
    this.$emit('focusout');
  }

  private handleCountryInputFocus() {
    this.fieldsFocused = true;
    this.countryInputFocused = true;
  }

  private handleCountryInputTabPress(event: Event) {
    event.preventDefault();
    this.postalCodeInput.focus();
  }

  private postalCodeFocusOutHandler() {
    this.syncedData.postalCode = stripUnoCharFromPostalCode(this.syncedData.postalCode);
    if (this.syncedData.postalCode) {
      this.syncedData.postalCode = this.syncedData.postalCode.toUpperCase();
    }

    this.focusOutHandler();
    if (this.postalCodeInput.valid) {
      this.$emit('autofill');
    }
  }

  private debouncedCollapseInputs = debounce(this.collapseInputs, 150);

  private collapseInputs(): void {
    if (this.autoCollapseEnabled && !this.syncedForceShowInputs) {
      this.fieldsFocused = false;
    }
  }

  private getCombinedAddress(): string {
    const data = [
      this.syncedData.addr1,
      this.syncedData.addr2,
      this.syncedData.addr3,
      this.syncedData.city,
      this.syncedData.province,
      this.syncedData.postalCode,
      this.currentCountry?.country,
    ];

    return data.filter(Boolean).join(', ') as string;
  }
}
